import { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAgentGroups } from '@/features/agent-groups/api';
import {
  useAllocatedDedicatedClis,
  usePromptUploads,
} from '@/features/voice-campaigns/api';
import { useSmsWebhookList } from '@/features/webhooks/api';
import type { InboundComposeFormValues } from '../../types';

interface Props {
  values: InboundComposeFormValues;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

export function StepReview({ values }: Props) {
  const prompts = usePromptUploads();
  const clis = useAllocatedDedicatedClis();
  const groups = useAgentGroups();
  const smsHooks = useSmsWebhookList();

  /** Map promptId → human label, for prompt-id review lines. */
  const promptLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of prompts.data ?? []) {
      map.set(
        String(p.promptId),
        p.fileName || p.promptName || `#${p.promptId}`,
      );
    }
    return (id: string) => map.get(id) ?? id;
  }, [prompts.data]);

  /** Map cliId → CLI string for the DIDs review line. */
  const cliLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clis.data ?? []) map.set(String(c.cliId), c.cli);
    return (id: string) => map.get(id) ?? id;
  }, [clis.data]);

  /** Map smsHookId → webhookName for SMS review lines. */
  const smsLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of smsHooks.data ?? []) map.set(String(w.id), w.webhookName);
    return (id: string) =>
      map.get(id) ?? (id.startsWith('http') ? id : id ? `#${id}` : '');
  }, [smsHooks.data]);

  void groups; // imported for parity with other steps — group names already on rows.

  const smsSummary = (flag: boolean, url: string) =>
    flag ? smsLabel(url) || 'enabled (no destination picked)' : 'off';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm the campaign before submitting. Click any earlier step to
          edit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="rounded-md border border-border bg-card px-3 py-1">
          <Row label="Name" value={values.campaignName} />
          <Row
            label="Retries"
            value={
              values.retries > 0
                ? `${values.retries} (every ${values.retryInterval} min)`
                : 'none'
            }
          />
          <Row label="Menu prompt" value={promptLabel(values.menuId)} />
          <Row
            label="No-agent prompt"
            value={promptLabel(values.noAgentId)}
          />
          {values.useWelcomeFile && (
            <Row label="Welcome" value={promptLabel(values.welcomeId)} />
          )}
          {values.useNoinputFile && (
            <Row label="No input" value={promptLabel(values.noinputId)} />
          )}
          {values.useWronginputFile && (
            <Row
              label="Wrong input"
              value={promptLabel(values.wronginputId)}
            />
          )}
          {values.useThanksFile && (
            <Row label="Thanks" value={promptLabel(values.thanksId)} />
          )}
          <Row
            label="Menu wait / re-prompt"
            value={`${values.menuWaitTime || '—'}s / ${values.rePrompt || '—'}`}
          />
          <Row
            label="Accepted DTMF"
            value={values.dtmf.length ? values.dtmf.join(', ') : ''}
          />
          <Row
            label="Agent groups"
            value={
              values.groupRows.length === 0 ? (
                ''
              ) : (
                <ul className="space-y-0.5">
                  {values.groupRows.map((r) => (
                    <li key={r.agentDtmf} className="flex items-baseline gap-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                        {r.agentDtmf}
                      </span>
                      <span>{r.groupName}</span>
                      <span className="text-xs text-muted-foreground">
                        #{r.groupId}
                      </span>
                    </li>
                  ))}
                </ul>
              )
            }
          />
          <Row
            label="DIDs (CLIs)"
            value={
              values.selectedCLI.length === 0
                ? ''
                : values.selectedCLI.map(cliLabel).join(', ')
            }
          />
          <Row
            label="SMS on success"
            value={smsSummary(values.sendSmsSuccess, values.smsApiSuccess)}
          />
          {values.sendSmsSuccess && (
            <Row
              label="Success min duration"
              value={`${values.callDurationSMS}s`}
            />
          )}
          <Row
            label="SMS on failure"
            value={smsSummary(values.sendSmsFail, values.smsApiFail)}
          />
          <Row
            label="SMS on DTMF"
            value={smsSummary(values.sendSmsDtmf, values.smsApiDtmf)}
          />
        </dl>
      </CardContent>
    </Card>
  );
}
